/**
 * Spectra V2 public market listing.
 *
 * Spectra (ex-APWine, npm org `perspectivefi`) is yield tokenisation with the
 * same shape as Pendle: an IBT (any yield-bearing ERC-4626) is split into a
 * **PT** — an ERC-5095 zero-coupon claim on the principal at `maturity` — and a
 * **YT** carrying the floating yield until then. The PT trades at a discount on
 * a rate-adjusted Curve StableSwap-NG pool, and that discount is the fixed rate.
 *
 * So this provider is the Pendle one with four substitutions, each of which is
 * a bug if assumed away — they are called out at the field that carries them:
 *
 *  1. the route key is a network NAME, not a chain id ({@link SPECTRA_NETWORKS});
 *  2. one request PER CHAIN, against Pendle's single global listing;
 *  3. rates arrive in PERCENT (Pendle's are fractions) and are APYs;
 *  4. `maturity` is unix SECONDS (Pendle's `expiry` is ISO-8601).
 *
 * **There is no documented API.** dev.spectra.finance publishes contracts,
 * oracles and MetaVaults; the endpoint below is the one app.spectra.finance
 * itself uses and its rate fields are defined nowhere. Everything asserted here
 * about their meaning was established by reproducing them from the published
 * prices — see {@link SpectraApiPool.impliedApy}.
 *
 * Docs: https://dev.spectra.finance/ · assessment: SPECTRA.md
 */

/**
 * Chain id → the network name the API routes on.
 *
 * **This mapping is hand-maintained and undiscoverable.** `/api/v1/1/pools` and
 * `/api/v1/ethereum/pools` both answer `400 {"error":"Invalid network"}`, and
 * there is no `/api/v1/networks` route to enumerate the valid names — so a new
 * Spectra deployment stays invisible until someone adds its name here. That is
 * the opposite of Pendle, whose global listing surfaces a new chain the moment
 * it exists.
 *
 * Verified live 2026-08-17: every name below answers 200. `polygon`, `linea`,
 * `gnosis`, `mode` and `fraxtal` are rejected, so Spectra is not on them under
 * any name we could find.
 */
export const SPECTRA_NETWORKS: Readonly<Record<string, string>> = {
  '1': 'mainnet',
  '10': 'optimism',
  '14': 'flare',
  '56': 'bsc',
  '143': 'monad',
  '146': 'sonic',
  '999': 'hyperevm',
  '8453': 'base',
  '42161': 'arbitrum',
  '43114': 'avalanche',
  '43111': 'hemi',
  '747474': 'katana',
}

export const spectraNetwork = (chainId: string): string | undefined =>
  SPECTRA_NETWORKS[String(chainId)]

export const hasSpectraMarkets = (chainId: string): boolean =>
  spectraNetwork(chainId) !== undefined

export const spectraPoolsUrl = (network: string): string =>
  `https://app.spectra.finance/api/v1/${network}/pools`

/** A token as the listing embeds it. Decimals are always present. */
export interface SpectraApiToken {
  address?: string | null
  chainId?: number | null
  name?: string | null
  symbol?: string | null
  decimals?: number | null
  logoURI?: string | null
  /** The yield venue behind the IBT, e.g. `Morpho`, `Yearn`, `SMARDEX`. */
  protocol?: string | null
  price?: { underlying?: number | null; usd?: number | null } | null
  /** IBT only — its own floating yield. **PERCENT**, and often `null`. */
  apr?: {
    total?: number | null
    details?: Record<string, number | null> | null
  } | null
  /** IBT price in underlying, 1e18-ish fixed point as a decimal string. */
  rate?: string | null
  spotRate?: string | null
}

/**
 * One entry of `market.pools[]` — the Curve StableSwap-NG pool the PT trades on.
 *
 * Every live market carries exactly one (36/36 at integration), but the field
 * is an array and is read as one: {@link pickPool} takes the deepest rather
 * than `[0]`, so a second pool on a market cannot silently decide the rate.
 */
export interface SpectraApiPool {
  address?: string | null
  chainId?: number | null
  /**
   * **The PT's fixed rate, as a compounded APY in PERCENT.**
   *
   * Established empirically, because nothing documents it: across all 36 live
   * markets on 7 chains this field reproduces
   * `((1 / ptPrice.underlying) ** (1 / yearsToMaturity) - 1) * 100`
   * to within float noise. It is the mid-price implied rate — the same quantity
   * as Pendle's `impliedApy`, in different units.
   *
   * See {@link ptApy} for the number that is NOT this.
   */
  impliedApy?: number | null
  /**
   * The rate a taker would actually realise — price impact and pool fee
   * included — as a compounded APY in PERCENT.
   *
   * **Not the market's rate, and not interchangeable with
   * {@link impliedApy}.** It is always the lower of the two, by an amount that
   * tracks pool thinness: 0.06 pp apart on the deepest market, 9.4 pp apart on
   * a $35 one, and on `PT-stXRP…2026/09/30` it is NEGATIVE (−0.06 %) while the
   * implied rate is +1.03 %. Carried through as
   * `SpectraPtMarket.executableApyPercent` because it is genuinely useful — it
   * is the honest "what you'd get" figure, which Pendle publishes nothing
   * equivalent to — but the row's `supplyRate` is the implied rate, so that a
   * Spectra row and a Pendle row mean the same thing when ranked together.
   */
  ptApy?: number | null
  /** LP-side total return, PERCENT. Neither PT nor YT earns this. */
  lpApy?: {
    total?: number | null
    details?: Record<string, number | null> | null
  } | null
  /** YT leverage multiple. Not a rate. */
  ytLeverage?: number | null
  liquidity?: { underlying?: number | null; usd?: number | null } | null
  ptPrice?: { underlying?: number | null; usd?: number | null } | null
  ytPrice?: { underlying?: number | null; usd?: number | null } | null
  /**
   * Curve's static pool fee — a **1e10-scaled integer string**, not a fraction
   * and not bps. `"1204544"` is 1.2 bps. `midFee` / `outFee` read 0 on every
   * live pool; `ibtToPtFee` / `ptToIbtFee` are the per-direction dynamic fees.
   */
  feeRate?: string | null
  midFee?: string | null
  outFee?: string | null
  ibtToPtFee?: string | null
  ptToIbtFee?: string | null
  /** `CURVE_SNG` on every live pool at integration. */
  type?: string | null
  lpt?: { address?: string | null; decimals?: number | null } | null
}

/** One `pools[]` entry — a MARKET (the naming is the API's, not ours). */
export interface SpectraApiMarket {
  /** The PrincipalToken contract. This is what a holder owns. */
  address?: string | null
  chainId?: number | null
  /** PT name, e.g. `Principal Token: sw-WUSDN(USDN) 2027/01/12`. */
  name?: string | null
  /** PT symbol, e.g. `PT-sw-WUSDN(USDN)-2027/01/12`. */
  symbol?: string | null
  /** **PT decimals** — not the underlying's. See `SpectraPtMarket.decimals`. */
  decimals?: number | null
  /** Unix SECONDS. */
  maturity?: number | null
  createdAt?: number | null
  /** `ptRate`, base-27. Starts at 1e27 and can only fall (negative-yield events). */
  rate?: string | null
  tvl?: {
    ibt?: number | null
    underlying?: number | null
    usd?: number | null
  } | null
  yt?: SpectraApiToken | null
  /** The interest-bearing token the PT is minted from. */
  ibt?: SpectraApiToken | null
  /** The vault behind a `Spectra4626Wrapper` IBT, when the IBT is a wrapper. */
  baseIbt?: SpectraApiToken | null
  /** What the PT redeems for at maturity, and the rate's denomination. */
  underlying?: SpectraApiToken | null
  pools?: SpectraApiPool[] | null
  maturityValue?: { underlying?: number | null; usd?: number | null } | null
  tags?: string[] | null
  multipliers?: unknown
}

const addr = (v: string | null | undefined): string | undefined =>
  typeof v === 'string' && /^0x[0-9a-fA-F]{40}$/.test(v)
    ? v.toLowerCase()
    : undefined

/** Lowercased address, or `undefined` for anything that is not one. */
export const spectraAddress = addr

/**
 * Is this market still live?
 *
 * **Judged against the clock, every time.** The listing appears to filter
 * matured markets already — zero of 36 had a past maturity on a protocol that
 * has been running V2 since 2024 — but that is an observation about today's
 * upstream behaviour, not a contract, and "the API already handles it" is
 * exactly the assumption `PENDLE_PT.md` §2 exists to refuse. A matured PT keeps
 * publishing its last pre-expiry implied APY, which on a rate-sorted earn list
 * puts a dead product on top.
 *
 * A market with no parseable maturity is NOT live: an unbounded fixed-rate row
 * is never the safe default.
 */
export function isLiveSpectraMarket(
  market: SpectraApiMarket,
  nowSecs: number = Math.floor(Date.now() / 1000),
): boolean {
  const maturity = market?.maturity
  if (typeof maturity !== 'number' || !Number.isFinite(maturity)) return false
  return maturity > nowSecs
}

/**
 * Pick the pool that prices the market.
 *
 * The DEEPEST, not the first. Every live market has exactly one pool today, so
 * this never fires — which is why it is written now rather than after a second
 * pool quietly halves a published rate.
 */
export function pickPool(
  pools: SpectraApiPool[] | null | undefined,
): SpectraApiPool | undefined {
  if (!Array.isArray(pools) || pools.length === 0) return undefined
  let best: SpectraApiPool | undefined
  let bestDepth = -Infinity
  for (const p of pools) {
    if (!p) continue
    const depth =
      (typeof p.liquidity?.usd === 'number' ? p.liquidity.usd : null) ??
      (typeof p.liquidity?.underlying === 'number'
        ? p.liquidity.underlying
        : null) ??
      0
    if (depth > bestDepth) {
      bestDepth = depth
      best = p
    }
  }
  return best ?? pools[0] ?? undefined
}

/**
 * Curve's 1e10-scaled fee integer → a FRACTION, the unit
 * `VaultTermInput.swapFeeRate` is documented in.
 */
export function parseCurveFee(
  raw: string | null | undefined,
): number | undefined {
  if (typeof raw !== 'string' || !/^\d+$/.test(raw)) return undefined
  const n = Number(raw) / 1e10
  return Number.isFinite(n) && n >= 0 && n < 1 ? n : undefined
}

/**
 * Bounds a published rate must clear to be believed, in PERCENT.
 *
 * **Spectra does not sanity-check its own rate fields.** HyperEVM publishes
 * `impliedApy: 549844464093797600` and `12307.1` on its two markets — raw fixed
 * point leaking through on pools holding $318 and $599 — and Hemi publishes
 * `null` for both rate fields. That is 3 of 36 markets, i.e. ~8 %, and an
 * unbounded value does not merely look odd: it takes the top of any
 * rate-sorted earn listing instantly.
 *
 * The floor is below zero on purpose. A PT trading ABOVE par is a real, if
 * unusual, market state and reporting it as such is correct; only the
 * impossible is rejected.
 */
export const SPECTRA_RATE_MIN_PERCENT = -99
export const SPECTRA_RATE_MAX_PERCENT = 1000

/**
 * A published rate, or `undefined` when it is absent or impossible.
 *
 * Callers DROP the market on `undefined` rather than substituting 0 — a
 * fixed-rate product whose rate we cannot establish is not an offer, and 0 %
 * is a specific claim rather than a neutral one.
 */
export function sanePercent(v: number | null | undefined): number | undefined {
  if (typeof v !== 'number' || !Number.isFinite(v)) return undefined
  if (v < SPECTRA_RATE_MIN_PERCENT || v > SPECTRA_RATE_MAX_PERCENT)
    return undefined
  return v
}

/**
 * `ptRate` at par, in the API's base-27 fixed point.
 *
 * The scale is INFERRED, not documented — `yield-calculations.md` names the
 * quantity and its direction but never its decimals. 33 of 36 live markets
 * publish exactly `1000000000000000000000000000`, which is 1.0 in base-27, the
 * scale Spectra uses elsewhere; the remaining three are the written-down ones.
 */
const PT_RATE_ONE = 1e27

/**
 * `ptRate` as a fraction of par (`1` = par, `0.5` = half).
 *
 * **This is the field that says a Spectra PT does NOT unconditionally redeem
 * 1:1.** Spectra's docs are explicit: "The `ptRate` starts as 1 and decreases
 * if the `ibtRate` … decreases. The `ptRate` can only decrease" — their own
 * worked example has a holder receive half their deposit back after the IBT
 * halves. Pendle carries the same economic exposure through its SY and
 * publishes no equivalent number, so this is strictly better disclosure, and it
 * is why the row surfaces {@link SpectraPtMarket.principalWriteDownBps}.
 *
 * It is also a live integrity check that costs nothing. The three markets whose
 * `impliedApy` is absent or nonsense (Hemi, both HyperEVM) are EXACTLY the
 * three whose `ptRate` has been written down to ~0 — two independent signals
 * agreeing that those rows are not offers.
 */
export function parsePtRate(raw: string | null | undefined): number | undefined {
  if (typeof raw !== 'string' || !/^\d+$/.test(raw)) return undefined
  const n = Number(raw) / PT_RATE_ONE
  // Above par is impossible by construction; treat it as a scale change we do
  // not understand rather than quietly reporting a bonus.
  if (!Number.isFinite(n) || n < 0 || n > 1.000001) return undefined
  return n
}

/**
 * In-isolate cache, keyed by NETWORK.
 *
 * Pendle caches one global listing; Spectra needs one entry per chain because
 * the endpoint is per chain. The PROMISE is cached, not the result, so
 * concurrent callers on the same chain share one in-flight request, and a
 * rejection evicts itself rather than poisoning the next 60 s.
 */
const CACHE_TTL_MS = 60_000

interface Cached {
  at: number
  promise: Promise<SpectraApiMarket[]>
}

const cache = new Map<string, Cached>()

/** Drop every cached listing. Tests only. */
export function clearSpectraMarketsCache(): void {
  cache.clear()
}

/**
 * Fetch one network's live PT listing.
 *
 * Chain filtering, maturity filtering and normalization happen in
 * `fetchPublic`. A non-200 throws — the caller logs and omits the provider for
 * that chain rather than serving a partial listing as a complete one.
 */
export function fetchSpectraApiMarkets(
  chainId: string,
): Promise<SpectraApiMarket[]> {
  const network = spectraNetwork(chainId)
  if (!network) return Promise.resolve([])

  const now = Date.now()
  const hit = cache.get(network)
  if (hit && now - hit.at < CACHE_TTL_MS) return hit.promise

  const promise = (async () => {
    const response = await fetch(spectraPoolsUrl(network), {
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) {
      throw new Error(
        `Spectra pools API failed for ${network}: ${response.status} - ${response.statusText}`,
      )
    }
    const json = await response.json()
    // The route answers a bare array on success and `{"error": …}` on an
    // unknown network — which is a 400, so this only guards a shape change.
    return Array.isArray(json) ? (json as SpectraApiMarket[]) : []
  })()

  cache.set(network, { at: now, promise })
  promise.catch(() => {
    if (cache.get(network)?.promise === promise) cache.delete(network)
  })
  return promise
}
